import React from 'react'
import { EmptyState } from '@enamelai/design-system'
import { useOutletContext } from 'react-router-dom'
import ArSummaryCards from '@components/common/ArSummary/ArSummaryCards.component'
import type { BillingOutletContext } from '@components/pages/Billing/Billing.component'
import LedgerTable from '@components/pages/Billing/Ledger/LedgerTable.component'
import {
  calculateAgingPeriods,
  resolveArAgingTotal,
} from '@utils/billingArAnalytics.util'
import { hasBillingListNarrowed } from '@utils/billing.utils'

function BillingLedgerPage(): React.ReactElement {
  const { hooks } = useOutletContext<BillingOutletContext>()

  const agingPeriods = React.useMemo(
    () => calculateAgingPeriods(hooks.arAgingReport),
    [hooks.arAgingReport],
  )

  const arAgingTotal = React.useMemo(
    () => resolveArAgingTotal(hooks.arAgingReport),
    [hooks.arAgingReport],
  )

  const listNarrowed = hasBillingListNarrowed(hooks.searchQuery, hooks.filters)
  const showEmptyState =
    !hooks.loadingLedger && hooks.filteredLedger.length === 0
  const hasNoRecords = !listNarrowed
  const showAnalytics = !showEmptyState || listNarrowed

  return (
    <div
      className={`billing-tab-panel${showEmptyState ? ' billing-tab-panel--empty ledger-table' : ''}`}
    >
      {showAnalytics ? (
        <ArSummaryCards
          patientAR={hooks.patientARAnalytics}
          insuranceAR={hooks.insuranceARAnalytics}
          agingPeriods={agingPeriods}
          arAgingTotal={arAgingTotal}
        />
      ) : null}

      {showEmptyState ? (
        <EmptyState
          iconClass="mgc mgc_bill_2_fill"
          title="No ledger entries found"
          description={
            hasNoRecords
              ? "You don't have any ledger entries on record yet."
              : 'No ledger entries match your search.'
          }
          actionLabel={hasNoRecords ? 'Create Invoice' : undefined}
          onAction={hasNoRecords ? hooks.handleCreateInvoice : undefined}
        />
      ) : (
        <LedgerTable
          filteredLedger={hooks.filteredLedger}
          ledgerCount={hooks.ledgerCount}
          currentPage={hooks.currentPage}
          pageSize={hooks.pageSize}
          handlePageChange={hooks.handlePageChange}
          handlePageSizeChange={hooks.handlePageSizeChange}
          handleTableSortChange={hooks.handleTableSortChange}
          selectedLedger={hooks.selectedLedger}
          showModal={hooks.showModal}
          handleView={hooks.handleView}
          handleModalClose={hooks.handleModalClose}
          relatedLedgerEntries={hooks.relatedLedgerEntries}
          handleCreateInvoiceFromLedger={hooks.handleCreateInvoiceFromLedger}
        />
      )}
    </div>
  )
}

export default BillingLedgerPage
